import React, { useEffect, useState } from "react";
import { Plus, Edit, MoreHorizontal, Lock, Unlock, Power, KeyRound, Trash2, UserPlus, History } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";

/*
 * Mirrors the API user (backendv2 UserResource / StoreUserRequest): a single
 * `name`, no first/last, no title/department — those columns don't exist on the
 * users table. `status` is absent on purpose: UpdateUserRequest rejects it, since
 * every status change has to go through an auditable lifecycle endpoint
 * (activate / deactivate / lock / unlock).
 */
const EMPTY_USER = {
  name: "",
  email: "",
  phone: "",
  role: "",
  facility_ids: [],
  license_number: "",
  license_expiry_date: "",
  password: "",
};

// DataTable reports sort direction as 1 / -1; the API takes asc / desc.
const sortDirection = (dir) => (dir === -1 ? "desc" : "asc");

// The API stores one `name`, so the avatar initials are derived, not stored.
function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);

  const [roles, setRoles] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState({ open: false });
  const [commsSheet, setCommsSheet] = useState({ open: false, user: null });

  async function load() {
    setLoading(true);
    try {
      const params = { page, per_page: pageSize, sort: sortBy, direction: sortDirection(sortDir) };
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/users", { params });
      // Paginated resource collection: { data: [...], meta: { total, ... } }.
      setItems(res.data.data || []);
      setTotal(res.data.meta?.total ?? 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, sortBy, sortDir, statusFilter]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {
    // Backend roles: GET /roles → { data: [{ id, name, display_name, ... }] }.
    api.get("/roles").then((r) => setRoles(r.data.data || [])).catch(() => {});
    api.get("/facilities", { params: { per_page: 50 } }).then((r) => setFacilities(r.data.data || [])).catch(() => {});
  }, []);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_USER);
    setErrors({});
    setSheetOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      name: row.name || "", email: row.email, phone: row.phone || "",
      role: row.role_code || "",
      // The API reads facilities back as objects and writes them as ids.
      facility_ids: (row.facilities || []).map((f) => String(f.id)),
      license_number: row.license_number || "",
      license_expiry_date: row.license_expiry_date || "",
      password: "",
    });
    setErrors({});
    setSheetOpen(true);
  }

  function validate() {
    const e = {};
    if (!form.email) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.role) e.role = "Role is required";
    if (!editing && !form.password) e.password = "An initial password is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        role: form.role,
        facility_ids: form.facility_ids.map(Number),
        license_number: form.license_number || null,
        license_expiry_date: form.license_expiry_date || null,
      };
      if (editing) {
        // Email is immutable in the UI; password and status are not accepted here
        // (reset-password and the lifecycle endpoints own them).
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...payload, email: form.email, password: form.password });
        toast.success("User created");
      }
      setSheetOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function doAction(row, action) {
    try {
      if (action === "delete") {
        await api.delete(`/users/${row.id}`);
        toast.success("User deleted");
      } else if (action === "reset") {
        // C2: the API mails a reset link and returns { ok: true }. It never hands
        // a temporary password back to the admin.
        await api.post(`/users/${row.id}/reset-password`);
        toast.success(`Password reset link sent to ${row.email}`);
      } else {
        await api.post(`/users/${row.id}/${action}`);
        toast.success(`User ${action}d`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const columns = [
    {
      key: "name", label: "Name", sortable: true,
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
            {initials(r.name)}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[#1F2937] truncate">{r.name}</div>
            <div className="text-xs text-[#6B7280] truncate">{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      // `role_code` is derived from the roles relation, so it isn't a sortable
      // column on the API (sort takes name | email | created_at | status).
      key: "role_code", label: "Role", sortable: false,
      render: (r) => (
        <span className="text-sm capitalize text-[#1F2937]">{(r.role_code || "—").replace(/_/g, " ")}</span>
      ),
    },
    {
      key: "license_number", label: "License", sortable: false,
      render: (r) => r.license_number || "—",
    },
    {
      key: "facilities", label: "Facilities", sortable: false,
      render: (r) => <span className="text-xs text-[#6B7280]">{(r.facilities || []).length} assigned</span>,
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (r) => <StatusBadge status={r.status} testId={`user-status-${r.id}`} />,
    },
    {
      key: "actions", label: "", sortable: false,
      render: (r) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`user-actions-${r.id}`} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`user-edit-${r.id}`}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCommsSheet({ open: true, user: r })} data-testid={`user-comms-${r.id}`}>
              <History className="w-4 h-4 mr-2" /> Communications
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => doAction(r, "reset")} data-testid={`user-reset-${r.id}`}>
              <KeyRound className="w-4 h-4 mr-2" /> Reset password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {r.status !== "active" && (
              <DropdownMenuItem onClick={() => doAction(r, "activate")}>
                <Power className="w-4 h-4 mr-2" /> Activate
              </DropdownMenuItem>
            )}
            {r.status === "active" && (
              <DropdownMenuItem onClick={() => doAction(r, "deactivate")}>
                <Power className="w-4 h-4 mr-2" /> Deactivate
              </DropdownMenuItem>
            )}
            {r.status !== "locked" ? (
              <DropdownMenuItem onClick={() => doAction(r, "lock")}>
                <Lock className="w-4 h-4 mr-2" /> Lock
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doAction(r, "unlock")}>
                <Unlock className="w-4 h-4 mr-2" /> Unlock
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirm({ open: true, title: "Delete user", description: `Soft-delete ${r.email}?`, onConfirm: () => doAction(r, "delete"), destructive: true })}
              data-testid={`user-delete-${r.id}`}
              className="text-[#EF4444]"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Manage clinicians, administrators, and staff across the H@H network."
        actions={
          <Button
            onClick={openCreate}
            data-testid="create-user-button"
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> New User
          </Button>
        }
      />

      <DataTable
        testId="users-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        sortBy={sortBy}
        sortDir={sortDir}
        exportFilename="users.csv"
        filters={
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="user-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? "Edit user" : "Create user"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update the user profile, role and assignments." : "Add a new user to the platform."}
            </SheetDescription>
          </SheetHeader>

          <div className="p-6 space-y-4">
            <FormSection title="General Information" testId="user-form-general" description="Basic identity and contact details.">
              <FormField label="Full name" required error={errors.name}>
                <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormField>
              <FormField label="Email" required error={errors.email}>
                <Input data-testid="user-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </FormField>
              <FormField label="Phone" hint="E.164 — used for SMS.">
                <Input data-testid="user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </FormField>
              <FormField label="License number">
                <Input data-testid="user-license-number" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
              </FormField>
              <FormField label="License expiry">
                <Input data-testid="user-license-expiry" type="date" value={form.license_expiry_date} onChange={(e) => setForm({ ...form, license_expiry_date: e.target.value })} />
              </FormField>
            </FormSection>

            <FormSection title="Assignment" testId="user-form-assignment" description="Role and facility assignments.">
              <FormField label="Role" required error={errors.role}>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.name}>{r.display_name || r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              {editing && (
                <FormField label="Status" hint="Changed from the row menu — Activate / Deactivate / Lock, so each change is audited.">
                  <Input disabled value={editing.status || "—"} data-testid="user-status-readonly" />
                </FormField>
              )}
              <FormField label="Facilities" full hint="Hold ⌘/Ctrl to select multiple">
                <select
                  multiple
                  data-testid="user-facilities"
                  value={form.facility_ids}
                  onChange={(e) => setForm({ ...form, facility_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                  className="w-full min-h-[100px] border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B7E8C]"
                >
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </FormField>
            </FormSection>

            {!editing && (
              <FormSection title="Configuration" testId="user-form-config" description="Initial credentials.">
                <FormField label="Initial password" full required error={errors.password} hint="Must satisfy the API password policy. The user is forced to change it on first login.">
                  <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </FormField>
              </FormSection>
            )}

            {editing && (
              <FormSection title="Audit Information" testId="user-form-audit" description="System-generated metadata.">
                <FormField label="Created">
                  <Input disabled value={editing.created_at ? new Date(editing.created_at).toLocaleString() : "—"} />
                </FormField>
                <FormField label="Last updated">
                  <Input disabled value={editing.updated_at ? new Date(editing.updated_at).toLocaleString() : "—"} />
                </FormField>
              </FormSection>
            )}
          </div>

          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} data-testid="user-form-cancel" className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="user-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title}
        description={confirm.description}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }}
        destructive={confirm.destructive}
      />

      <UserCommsSheet
        open={commsSheet.open}
        onOpenChange={(o) => setCommsSheet((s) => ({ ...s, open: o }))}
        user={commsSheet.user}
      />
    </div>
  );
}

// ---------- User Communications Sheet (G5) ----------
function UserCommsSheet({ open, onOpenChange, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("teleconsults");

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/users/${user.id}/communications`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;

  const teleStatusColor = {
    scheduled: "#3B82F6", in_progress: "#10B981", completed: "#475569",
    cancelled: "#EF4444", no_show: "#F97316", awaiting_participants: "#F59E0B",
  };
  const smsStatusColor = {
    sent: "#10B981", failed: "#EF4444", mocked: "#94A3B8", skipped: "#F59E0B", queued: "#3B82F6",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[760px] overflow-y-auto p-0 bg-[#FAFBFC]" data-testid="user-comms-sheet">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Communications — {user.name}</SheetTitle>
          <SheetDescription>
            Teleconsultations · SMS · Notifications sent to {user.email}
            {user.phone ? ` · ${user.phone}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="p-6">
          {loading || !data ? (
            <div className="text-sm text-[#6B7280] p-8 text-center" data-testid="user-comms-loading">Loading communications…</div>
          ) : (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <CommsKPI label="Teleconsults" value={data.counts.teleconsults} testId="kpi-tele" />
                <CommsKPI label="SMS Messages" value={data.counts.sms_messages} testId="kpi-sms" />
                <CommsKPI label="SMS Failed" value={data.counts.sms_failed} accent="#EF4444" testId="kpi-sms-failed" />
                <CommsKPI label="Notifications" value={data.counts.notifications} testId="kpi-notifs" />
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="teleconsults" data-testid="user-comms-tab-tele">Teleconsults ({data.counts.teleconsults})</TabsTrigger>
                  <TabsTrigger value="sms" data-testid="user-comms-tab-sms">SMS ({data.counts.sms_messages})</TabsTrigger>
                  <TabsTrigger value="notifications" data-testid="user-comms-tab-notif">Notifications ({data.counts.notifications})</TabsTrigger>
                </TabsList>

                <TabsContent value="teleconsults" className="mt-3">
                  {data.teleconsultations.length === 0 ? (
                    <Empty label="No teleconsultations for this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-tele-list">
                      {data.teleconsultations.map((t) => (
                        <a
                          key={t.id} href={t.action_url}
                          className="block p-3 hover:bg-[#F9FAFB] transition"
                          data-testid={`user-comms-tele-${t.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937]">{t.title}</div>
                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider text-white" style={{ background: teleStatusColor[t.status] || "#94A3B8" }}>
                              {(t.status || "").replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="text-xs text-[#6B7280] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span>Patient: <span className="text-[#1F2937]">{t.patient_name || "—"}</span> {t.patient_mrn && <span className="font-mono text-[#94A3B8]">{t.patient_mrn}</span>}</span>
                            <span>Role: <span className="capitalize">{(t.user_relation || "").replace(/_/g, " ")}</span></span>
                            {t.invite_status && <span>Invite: <span className="capitalize">{t.invite_status}</span></span>}
                            {t.source_module && <span>Source: <span className="uppercase tracking-wider">{t.source_module}</span></span>}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1">
                            {t.scheduled_at ? `scheduled ${new Date(t.scheduled_at).toLocaleString()}` : (t.created_at ? `created ${new Date(t.created_at).toLocaleString()}` : "")}
                            {t.ended_at && <> · ended {new Date(t.ended_at).toLocaleString()}</>}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="sms" className="mt-3">
                  {data.sms_messages.length === 0 ? (
                    <Empty label="No SMS messages sent to this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-sms-list">
                      {data.sms_messages.map((s) => (
                        <div key={s.id} className="p-3" data-testid={`user-comms-sms-${s.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937] text-sm">
                              {(s.event_type || "").replace(/_/g, " ")}
                              <span className="ml-2 text-xs font-normal text-[#94A3B8]">→ {s.recipient || s.recipient_raw || "—"}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider text-white" style={{ background: smsStatusColor[s.status] || "#94A3B8" }}>
                              {s.status}
                            </span>
                          </div>
                          {s.body && <div className="mt-1 text-xs text-[#475569] whitespace-pre-wrap">{s.body}</div>}
                          {s.error && <div className="mt-1 text-xs text-[#EF4444]">Error: {s.error}</div>}
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1">
                            {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notifications" className="mt-3">
                  {data.notifications.length === 0 ? (
                    <Empty label="No in-app notifications for this user." />
                  ) : (
                    <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="user-comms-notif-list">
                      {data.notifications.map((n) => (
                        <div key={n.id} className="p-3" data-testid={`user-comms-notif-${n.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-[#1F2937] text-sm">{n.title || n.event}</div>
                            {n.severity && <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{n.severity}</span>}
                          </div>
                          {n.body && <div className="mt-1 text-xs text-[#475569]">{n.body}</div>}
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-1 flex items-center gap-2">
                            <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
                            {n.is_read ? <span className="text-[#10B981]">read</span> : <span className="text-[#F59E0B]">unread</span>}
                            {n.action_url && <a href={n.action_url} className="text-[#0B7E8C] hover:underline">open ↗</a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CommsKPI({ label, value, accent = "#0B7E8C", testId }) {
  return (
    <div className="hah-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className="text-2xl font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="hah-card p-8 text-center text-sm text-[#6B7280]">{label}</div>;
}
